import 'requestidlecallback-polyfill';
import { create } from '@thoughtspot/logger';
import _ from 'lodash';
import { DEFAULT_ANGULAR_ROTATION_IN_DEG } from './highchart-theme-screen-util';
import { isXAxisVertical } from '../core/chart-type-specification-service';
import { getHighchartChartType } from './blink-highchart-config-util';
const logger = create('highchart-label-processor');
const paddingBetweenLabels = 3;
function isLabelOnLabel(label1, label2, padding = 0) {
    const textElement1 = label1.text ? label1.text : label1;
    const textElement2 = label2.text ? label2.text : label2;
    const a = textElement1.element.getBoundingClientRect();
    const b = textElement2.element.getBoundingClientRect();
    const isBRightOfA = b.left > a.right + padding;
    const isBLeftOfA = b.right < a.left - padding;
    const isBBelowA = b.top > a.bottom + padding;
    const isBAboveA = b.bottom < a.top - padding;
    window.requestIdleCallback(() => logger.debug(a, b, label1.textStr, label2.textStr, !(isBRightOfA || isBLeftOfA || isBBelowA || isBAboveA)));
    return !(isBRightOfA || isBLeftOfA || isBBelowA || isBAboveA);
}
export function postProcessHighchartXAxisLabels(chart) {
    var _a, _b;
    const xAxis = (_a = chart.xAxis) === null || _a === void 0 ? void 0 : _a[0];
    if (!xAxis || !xAxis.ticks) {
        return;
    }
    const startTime = performance.now();
    if (xAxis.autoRotation) {
        const scrollbar = xAxis.userOptions.scrollbar;
        if (scrollbar.enabled) {
            if (!((_b = xAxis.userOptions.title) === null || _b === void 0 ? void 0 : _b.offset)) {
                if (xAxis.axisTitleMargin && xAxis.axisTitle) {
                    chart.update({
                        xAxis: {
                            title: {
                                offset: xAxis.axisTitleMargin,
                            },
                            labels: {
                                style: {
                                    width: xAxis.maxLabelLength + 20,
                                    textOverflow: 'ellipsis',
                                },
                            },
                        },
                    });
                }
            }
            else if (xAxis.userOptions.labels.style.textOverflow !== 'ellipsis' &&
                (_.isNil(xAxis.userOptions.labels.style.width) ||
                    Number.isNaN(xAxis.userOptions.labels.style.width))) {
                chart.update({
                    xAxis: {
                        labels: {
                            style: {
                                width: (xAxis.bottom -
                                    xAxis.userOptions.scrollbar.height) *
                                    Math.sin(Math.abs(DEFAULT_ANGULAR_ROTATION_IN_DEG) *
                                        (Math.PI / 180)),
                                textOverflow: 'ellipsis',
                            },
                        },
                    },
                });
            }
        }
    }
    else {
        if (xAxis.bottom > chart.chartHeight * 0.4) {
            if (xAxis.userOptions.labels.style.textOverflow !== 'ellipsis') {
                chart.update({
                    xAxis: {
                        labels: {
                            style: {
                                textOverflow: 'ellipsis',
                            },
                        },
                    },
                });
            }
        }
        const ticks = Object.values(chart.xAxis[0].ticks);
        if (!isXAxisVertical(getHighchartChartType(chart))) {
            const ellipsisedTicks = ticks.filter(tick => { var _a, _b; return ((_b = (_a = tick.label) === null || _a === void 0 ? void 0 : _a.styles) === null || _b === void 0 ? void 0 : _b.textOverflow) === 'ellipsis'; });
            const isMostOfTheLabelsEllipsised = ellipsisedTicks.length > ticks.length / 2;
            if (isMostOfTheLabelsEllipsised) {
                chart.update({
                    xAxis: {
                        labels: {
                            rotation: DEFAULT_ANGULAR_ROTATION_IN_DEG,
                        },
                    },
                });
                return;
            }
        }
        if (xAxis.userOptions.labels.autoRotationLimit) {
            return;
        }
        let prevLabel = null;
        let overlapDetected = false;
        let autoRotationLimit = chart.xAxis[0].maxLabelLength;
        for (const tick in ticks) {
            if (ticks.hasOwnProperty(tick)) {
                const currentLabel = ticks[tick].label;
                if (prevLabel && currentLabel) {
                    if (isLabelOnLabel(prevLabel, currentLabel, paddingBetweenLabels)) {
                        overlapDetected = true;
                        autoRotationLimit = Math.ceil(prevLabel.element.getBoundingClientRect().width +
                            paddingBetweenLabels);
                        break;
                    }
                }
                prevLabel = currentLabel;
            }
        }
        if (overlapDetected &&
            chart.xAxis[0].options.labels.autoRotationLimit !==
                autoRotationLimit) {
            chart.xAxis[0].update({
                labels: {
                    autoRotationLimit,
                },
            });
        }
    }
    const endTime = performance.now();
    window.requestIdleCallback(() => logger.log('postProcessHighchartXAxisLabels perf time', endTime - startTime, 'ms'));
}
export function postProcessHighchartDataLabels(chart) {
    var _a;
    const visibleStackLabels = [];
    chart.yAxis.forEach(yAxis => {
        var _a, _b, _c;
        const stacks = yAxis.stacks || yAxis.stacking.stacks;
        const columns = ((_a = Object.entries(stacks).find(([key]) => key.startsWith('column'))) === null || _a === void 0 ? void 0 : _a[1]) ||
            ((_b = Object.entries(stacks).find(([key]) => key.startsWith('area'))) === null || _b === void 0 ? void 0 : _b[1]) ||
            ((_c = Object.entries(stacks).find(([key]) => key.startsWith('bar'))) === null || _c === void 0 ? void 0 : _c[1]) ||
            {};
        _.forIn(columns, stackItem => {
            if (!stackItem.label || stackItem.label.visibility === 'hidden') {
                return;
            }
            let overlap = false;
            visibleStackLabels.forEach(visibleStackLabel => {
                if (isLabelOnLabel(stackItem.label, visibleStackLabel, paddingBetweenLabels)) {
                    overlap = true;
                }
            });
            if (overlap) {
                stackItem.label.hide();
            }
            else {
                stackItem.label.element.style.opacity = 1;
                visibleStackLabels.push(stackItem.label);
            }
        });
    });
    (_a = chart === null || chart === void 0 ? void 0 : chart.series) === null || _a === void 0 ? void 0 : _a.forEach(serie => {
        const isLabelEnabled = serie.options.dataLabels.enabled;
        if (serie && serie.points && isLabelEnabled && serie.visible) {
            serie.points.forEach(point => {
                var _a;
                let hidePoint = false;
                if (!point.dataLabel || point.isInside === false) {
                    return;
                }
                visibleStackLabels.some(visibleStackLabel => {
                    var _a;
                    if (isLabelOnLabel(point.dataLabel, visibleStackLabel, paddingBetweenLabels)) {
                        point.dataLabel.hide();
                        (_a = point.connector) === null || _a === void 0 ? void 0 : _a.hide();
                        hidePoint = true;
                        return true;
                    }
                    return false;
                });
                if (!hidePoint) {
                    point.dataLabel.show();
                    (_a = point.connector) === null || _a === void 0 ? void 0 : _a.show();
                    visibleStackLabels.unshift(point.dataLabel);
                }
            });
        }
    });
}
//# sourceMappingURL=highcharts-label-processor-service.js.map